import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import * as Yup from "yup";
import { BRAND_BLUE } from "../../shared/pricingHelpers";

export interface BonusRuleValues {
  currency: string;
  min_amount: number | "";
  bonus_percent: number | "";
  is_active: boolean;
}

const EMPTY: BonusRuleValues = {
  currency: "USD",
  min_amount: "",
  bonus_percent: "",
  is_active: true,
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<BonusRuleValues> | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
}

const schema = Yup.object().shape({
  currency: Yup.string().oneOf(["USD", "INR"]).required("Currency is required"),
  min_amount: Yup.number()
    .typeError("Min amount is required")
    .required("Min amount is required")
    .min(0, "Must be ≥ 0"),
  bonus_percent: Yup.number()
    .typeError("Bonus percent is required")
    .required("Bonus percent is required")
    .moreThan(0, "Must be > 0")
    .max(100, "Must be ≤ 100"),
});

const buildPayload = (v: BonusRuleValues) => ({
  currency: v.currency,
  min_amount: Number(v.min_amount),
  bonus_percent: Number(v.bonus_percent),
  is_active: v.is_active,
});

const BonusRulesForm: React.FC<Props> = ({
  open,
  mode,
  initial,
  saving,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<BonusRuleValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY, ...(initial || {}) } as BonusRuleValues);
      setErrors({});
    }
  }, [open, initial]);

  const set = (name: keyof BonusRuleValues, value: any) =>
    setValues((p) => ({ ...p, [name]: value }));

  const handleSubmit = async () => {
    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      onSubmit(buildPayload(values));
    } catch (err: any) {
      const e: Record<string, string> = {};
      err.inner?.forEach((x: any) => (e[x.path] = x.message));
      setErrors(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {mode === "create" ? "Add Bonus Rule" : "Edit Bonus Rule"}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="bonus-currency">Currency</InputLabel>
            <Select
              labelId="bonus-currency"
              label="Currency"
              value={values.currency}
              onChange={(e) => set("currency", e.target.value)}
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="INR">INR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Min Amount"
            type="number"
            value={values.min_amount}
            onChange={(e) =>
              set("min_amount", e.target.value === "" ? "" : Number(e.target.value))
            }
            error={Boolean(errors.min_amount)}
            helperText={errors.min_amount || "Bonus applies when paid amount exceeds this"}
            size="small"
            fullWidth
          />
          <TextField
            label="Bonus Percent"
            type="number"
            value={values.bonus_percent}
            onChange={(e) =>
              set("bonus_percent", e.target.value === "" ? "" : Number(e.target.value))
            }
            error={Boolean(errors.bonus_percent)}
            helperText={errors.bonus_percent || "e.g. 5 for +5% credits"}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={values.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: BRAND_BLUE,
            "&:hover": { backgroundColor: "#0445a3" },
          }}
        >
          {mode === "create" ? "Create" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BonusRulesForm;
